import React, { useState, useEffect } from 'react';
import { Trash2, AlertTriangle, DollarSign } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import { Product, ProductLocation } from '../../types';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Select from '../ui/Select';

interface TransferToWasteFormProps {
  sourceType: 'supplies' | 'manufactured_products';
  sourceItem: Product;
  productLocations?: ProductLocation[];
  onTransferComplete: () => void;
  onCancel: () => void;
}

interface LocationQuantity {
  location_id: string;
  location_name: string;
  available_quantity: number;
  transfer_quantity: number;
}

const TransferToWasteForm: React.FC<TransferToWasteFormProps> = ({
  sourceType,
  sourceItem,
  productLocations = [],
  onTransferComplete,
  onCancel
}) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [locationQuantities, setLocationQuantities] = useState<LocationQuantity[]>([]);

  const [formData, setFormData] = useState({
    reason: '',
    notes: '',
    salvage_value: 0,
    disposal_cost: 0
  });

  const reasons = [
    { value: 'discontinued', label: 'Discontinued' },
    { value: 'obsolete', label: 'Obsolete' },
    { value: 'damaged', label: 'Damaged' },
    { value: 'expired', label: 'Expired' },
    { value: 'failed_qa', label: 'Failed Quality Assurance' },
    { value: 'excess', label: 'Excess Inventory' },
    { value: 'other', label: 'Other' }
  ];

  useEffect(() => {
    if (productLocations.length > 0) {
      setLocationQuantities(productLocations.map(pl => ({
        location_id: pl.location_id,
        location_name: pl.location?.name || 'Unknown Location',
        available_quantity: pl.quantity,
        transfer_quantity: 0
      })));
    } else {
      setLocationQuantities([{
        location_id: 'default',
        location_name: sourceItem.location || 'Default Location',
        available_quantity: sourceItem.stock_quantity || 0,
        transfer_quantity: 0
      }]);
    }
  }, [productLocations, sourceItem]);

  const handleLocationQuantityChange = (location_id: string, quantity: number) => {
    setLocationQuantities(prev => prev.map(lq =>
      lq.location_id === location_id
        ? { ...lq, transfer_quantity: Math.min(quantity, lq.available_quantity) }
        : lq
    ));
  };

  const getTotalTransferQuantity = () => {
    return locationQuantities.reduce((total, lq) => total + lq.transfer_quantity, 0);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const totalQuantity = getTotalTransferQuantity();

    if (totalQuantity === 0) {
      alert('Please specify quantities to transfer from at least one location');
      return;
    }

    if (!formData.reason) {
      alert('Please select a reason for transfer to waste');
      return;
    }

    try {
      setLoading(true);

      const locationQuantitiesForDB = locationQuantities
        .filter(lq => lq.transfer_quantity > 0)
        .map(lq => ({
          location_id: lq.location_id,
          location_name: lq.location_name,
          quantity: lq.transfer_quantity
        }));

      const { data, error } = await supabase.rpc('transfer_to_waste', {
        p_user_id: user?.id,
        p_source_type: sourceType,
        p_source_id: sourceItem.id,
        p_location_quantities: locationQuantitiesForDB,
        p_reason: formData.reason,
        p_notes: formData.notes || null,
        p_salvage_value: formData.salvage_value || 0,
        p_disposal_cost: formData.disposal_cost || 0
      });

      if (error) throw error;

      alert(`Successfully transferred ${totalQuantity} units to R&D Waste Management`);
      onTransferComplete();
    } catch (error: any) {
      console.error('Error transferring to waste:', error);
      alert(error.message || 'Failed to transfer to waste. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="w-full max-w-2xl">
      <CardHeader>
        <div className="flex items-center">
          <Trash2 className="h-5 w-5 text-red-600 mr-2" />
          <CardTitle>Transfer to R&D Waste Management</CardTitle>
        </div>
      </CardHeader>
      <form onSubmit={handleSubmit}>
        <CardContent className="space-y-6">
          <div className="bg-blue-50 p-4 rounded-lg">
            <h4 className="font-medium text-blue-900 mb-2">{sourceItem.name}</h4>
            <p className="text-sm text-blue-700">SKU: {sourceItem.sku}</p>
            <p className="text-sm text-blue-700">
              {sourceType === 'supplies' ? 'Supply Item' : 'Manufactured Product'}
            </p>
          </div>

          <div className="bg-amber-50 border border-amber-200 p-4 rounded-lg">
            <div className="flex items-start">
              <AlertTriangle className="h-5 w-5 text-amber-600 mr-2 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-amber-900">
                  Transfer to Waste Management
                </p>
                <p className="text-sm text-amber-700 mt-1">
                  This will move the specified quantities from active inventory to R&D Waste Management.
                  You can transfer items back later if needed.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h4 className="font-medium text-gray-900">Select Quantities by Location</h4>
            {locationQuantities.map(lq => (
              <div key={lq.location_id} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">{lq.location_name}</span>
                  <span className="text-sm text-gray-500">
                    Available: {lq.available_quantity} units
                  </span>
                </div>
                <Input
                  type="number"
                  min="0"
                  max={lq.available_quantity}
                  value={lq.transfer_quantity}
                  onChange={(e) => handleLocationQuantityChange(lq.location_id, parseInt(e.target.value) || 0)}
                  placeholder="Quantity to transfer"
                  fullWidth
                />
              </div>
            ))}

            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="text-sm font-medium text-gray-900">
                Total Transfer Quantity: <span className="text-lg">{getTotalTransferQuantity()}</span> units
              </p>
            </div>
          </div>

          <Select
            label="Reason for Transfer"
            options={[
              { value: '', label: 'Select a reason' },
              ...reasons
            ]}
            value={formData.reason}
            onChange={(value) => setFormData({ ...formData, reason: value })}
            required
            fullWidth
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Transfer Notes
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-blue-900"
              placeholder="Add any additional details about this transfer..."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <DollarSign className="inline h-4 w-4" /> Estimated Salvage Value
              </label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={formData.salvage_value}
                onChange={(e) => setFormData({ ...formData, salvage_value: parseFloat(e.target.value) || 0 })}
                placeholder="0.00"
                fullWidth
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                <DollarSign className="inline h-4 w-4" /> Estimated Disposal Cost
              </label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={formData.disposal_cost}
                onChange={(e) => setFormData({ ...formData, disposal_cost: parseFloat(e.target.value) || 0 })}
                placeholder="0.00"
                fullWidth
              />
            </div>
          </div>
        </CardContent>

        <div className="px-6 py-4 bg-gray-50 flex justify-end space-x-2">
          <Button
            type="button"
            variant="secondary"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            isLoading={loading}
            disabled={getTotalTransferQuantity() === 0}
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Transfer to Waste
          </Button>
        </div>
      </form>
    </Card>
  );
};

export default TransferToWasteForm;
